import { dataControllersClient } from '@/helpers/api/apiClients'
import {
	PlacesAutocompleteRequest,
	PlacesDetailsRequest,
	PlacesLookupType,
	type PlacesDetailsResponse,
	type PlacesSuggestionResponse,
} from '@/helpers/api/WebApiClient'

export const PLACES_MIN_INPUT_LENGTH = 2
export const PLACES_DEBOUNCE_MS = 800
export const PLACES_TOO_MANY_MESSAGE = 'Too many lookups. Please wait a moment.'
export const PLACES_UNAVAILABLE_MESSAGE = 'City and postal lookup is temporarily unavailable. Please try again.'
export const PLACES_SELECT_CITY_MESSAGE = 'Select a city from the list'
export const PLACES_INVALID_CITY_MESSAGE = 'Enter a valid city name'
export const PLACES_INVALID_POSTAL_MESSAGE = 'Enter a valid postal code'

export class PlacesLookupError extends Error {
	status: number

	constructor(message: string, status: number) {
		super(message)
		this.name = 'PlacesLookupError'
		this.status = status
	}
}

export const createPlacesSessionToken = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}

	return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const debounce = <Args extends unknown[]>(fn: (...args: Args) => void, wait: number) => {
	let timer: ReturnType<typeof setTimeout> | undefined

	const debounced = (...args: Args) => {
		if (timer) {
			clearTimeout(timer)
		}

		timer = setTimeout(() => {
			fn(...args)
		}, wait)
	}

	debounced.cancel = () => {
		if (timer) {
			clearTimeout(timer)
			timer = undefined
		}
	}

	return debounced
}

interface PlacesSearchParams {
	input: string
	type: PlacesLookupType
	countryId: number
	stateId: number
	city?: string
	sessionToken: string
	defaults?: boolean
}

const toPlacesError = (error: unknown) => {
	if (error instanceof PlacesLookupError) {
		return error
	}

	const result = error as { statusCode?: number; status?: number; exception?: string; messages?: string[] } | string | null
	if (result == null || result === '') {
		return new PlacesLookupError(PLACES_UNAVAILABLE_MESSAGE, 503)
	}

	if (typeof result === 'string') {
		return new PlacesLookupError(result, 503)
	}

	const status = result.statusCode ?? result.status
	if (status === 429) {
		return new PlacesLookupError(PLACES_TOO_MANY_MESSAGE, 429)
	}

	const message = result.messages?.[0] || result.exception?.split('\n')[0] || PLACES_UNAVAILABLE_MESSAGE
	return new PlacesLookupError(message, status ?? 503)
}

export const searchPlaces = async ({ input, type, countryId, stateId, city, sessionToken, defaults }: PlacesSearchParams): Promise<PlacesSuggestionResponse[]> => {
	const trimmed = input.trim()
	if ((!defaults && trimmed.length < PLACES_MIN_INPUT_LENGTH) || countryId <= 0 || stateId <= 0) {
		return []
	}

	try {
		const response = await dataControllersClient.placesAutocomplete(
			new PlacesAutocompleteRequest({
				input: trimmed,
				type,
				countryId,
				stateId,
				city: city?.trim() || undefined,
				sessionToken,
				defaults: defaults === true,
			})
		)
		return response.suggestions ?? []
	} catch (error) {
		throw toPlacesError(error)
	}
}

export const confirmPlace = async ({
	placeId,
	type,
	countryId,
	stateId,
	city,
	sessionToken,
}: Omit<PlacesSearchParams, 'input' | 'defaults'> & { placeId: string }): Promise<PlacesDetailsResponse> => {
	try {
		return await dataControllersClient.placesDetails(
			new PlacesDetailsRequest({
				placeId,
				type,
				countryId,
				stateId,
				city: city?.trim() || undefined,
				sessionToken,
			})
		)
	} catch (error) {
		throw toPlacesError(error)
	}
}

export const suggestionMatchesInput = (suggestion: PlacesSuggestionResponse, input: string) => {
	const normalized = input.trim().toLowerCase()
	return suggestion.mainText?.trim().toLowerCase() === normalized || suggestion.text?.trim().toLowerCase() === normalized
}
